import React, { Component, createContext } from 'react'
import { withRouter } from 'next/router'


//* Providers
import { ConfigProvider } from 'antd'
import { ThemeProvider } from 'styled-components'

//* Helpers
import { routerToPathAndQuery, validateMessages } from 'helpers'

//* Styles
import theme from 'styles/common/theme'
import HelperClass from 'styles/common/helperClass'
import Variables from 'styles/common/variables'
import Typography from 'styles/common/typography'
import FormStyle from 'styles/common/formStyle'
import PopupStyle from 'styles/common/popupStyle'


const UIContext = createContext(null)
export const UIConsumer = UIContext.Consumer

class UIProvider extends Component {

    //! States
    state = {
        popupIsOpen: false,
        popupComponent: null,
        popupProps: {},
        preloader: true,
        winWidth: 1920,
        winHeight: 1080,
        screenSizes: {
            screen3XL: '',
            screen2XL: '',
            screenXL: '',
            screenL: '',
            screenM: '',
            screenS: '',
            screenXS: '',
        },
        colors: {},
        contactPopupIsOpen: false,
        deactivatePopupIsOpen: false,
        btnLoading: false,
        proConPopupIsOpen: false,
        proConPopupBtnColor: '',
        proConType: null,
        followersPopupIsOpen: false,
        deleteDraftPopup: false,
        reportPopup: false
    }

    //! Methods
    methods = {
        openPopup: this.openPopup.bind(this),
        closePopup: this.closePopup.bind(this),
        disableDomScroll: this.disableDomScroll.bind(this),
        enableDomScroll: this.enableDomScroll.bind(this),
        loadingBtnLogic: this.loadingBtnLogic.bind(this),

        toggleContactPopup: this.toggleContactPopup.bind(this),
        toggleDeactivatePopup: this.toggleDeactivatePopup.bind(this),
        toggleConProPopup: this.toggleConProPopup.bind(this),
        toggleFollowersPopup: this.toggleFollowersPopup.bind(this),
    }


    //! Popups
    popups = ['login', 'sign-up', 'forgot-password', 'reset-password']

    screenResize = () => {
        this.setState({
            winWidth: window.innerWidth,
            winHeight: window.innerHeight,
        })
    }

    getScreenSizes() {
        const root = getComputedStyle(document.documentElement)

        const screenSizes = {
            screen3XL: parseInt(root.getPropertyValue('--screen3XL')),
            screen2XL: parseInt(root.getPropertyValue('--screen2XL')),
            screenXL: parseInt(root.getPropertyValue('--screenXL')),
            screenL: parseInt(root.getPropertyValue('--screenL')),
            screenM: parseInt(root.getPropertyValue('--screenM')),
            screenS: parseInt(root.getPropertyValue('--screenS')),
            screenXS: parseInt(root.getPropertyValue('--screenXS')),
        }

        const colors = {}

        this.setState({ screenSizes, colors })
    }

    //! Open Popup
    openPopup(popupComponent = null, popupProps = {}) {
        if (typeof popupComponent === 'string' && this.popups.includes(popupComponent) && this.props.router.query?.c !== popupComponent) {
            const { pathname, query } = routerToPathAndQuery(this.props.router, 'c', popupComponent)
            this.props.router.push({ pathname, query }, undefined, { shallow: true })
        }
        this.disableDomScroll()

        this.setState({
            popupIsOpen: true,
            popupComponent,
            popupProps
        })
    }

    //!Button logic saved
    loadingBtnLogic() {
        this.setState({
            btnLoading: !this.state.btnLoading,
        })
    }

    //! Followers Popup
    toggleFollowersPopup() {
        this.setState({
            followersPopupIsOpen: !this.state.followersPopupIsOpen,
        })
    }

    //! Close Popup
    closePopup() {
        const { pathname, query } = routerToPathAndQuery(this.props.router, 'c')
        this.props.router.push({ pathname, query }, undefined, { scroll: false })

        this.setState({
            popupIsOpen: false,
            popupComponent: null,
        }, () => {
            this.enableDomScroll();
        })
    }

    //! Query Popups Checking and Opening
    queryPopupsCheckAndOpen() {
        const popupQuery = this.props.router.query.c

        if (popupQuery && this.popups.includes(popupQuery) && this.state.popupComponent !== popupQuery) {
            this.openPopup(popupQuery)
        }
    }

    //! Query Popups Checking and Closing
    queryPopupsCheckAndClose(prevProps) {
        const popupQuery = this.props.router.query?.c

        if (!popupQuery && this.state.popupIsOpen && this.popups.includes(this.state.popupComponent)) {
            this.setState({
                popupIsOpen: false,
                popupComponent: null,
            }, () => {
                this.enableDomScroll();
            })
        }
    }

    toggleContactPopup() {
        this.setState({
            contactPopupIsOpen: !this.state.contactPopupIsOpen,
        })
    }

    toggleDeactivatePopup() {
        this.setState({
            deactivatePopupIsOpen: !this.state.deactivatePopupIsOpen,
        })
    }

    toggleConProPopup(proConType, btnColor) {
        this.setState({
            proConPopupIsOpen: !this.state.proConPopupIsOpen,
            proConPopupBtnColor: btnColor,
            proConType: proConType
        })
    }

    //! Disable DOM Scroll
    disableDomScroll() {
        // document.querySelector('html').classList.add('hide-scroll')

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

        // if any scroll is attempted, set this to the previous value
        window.onscroll = function (e) {
            e.preventDefault()
            window.scrollTo(scrollLeft, scrollTop)
        }
    }

    //! Enable DOM Scroll
    enableDomScroll() {
        // document.querySelector('html').classList.remove('hide-scroll');

        const scrollTop = window.pageYOffset || document.documentElement.scrollTop
        const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft

        window.onscroll = function () {
        }
        window.scrollTo(scrollLeft, scrollTop)
    }

    //! Component Did Mount
    componentDidMount() {
        window.addEventListener('resize', this.screenResize)

        this.getScreenSizes()
        this.screenResize()

        this.queryPopupsCheckAndOpen()
        this.props.router.events.on('routeChangeComplete', this.queryPopupsCheckAndClose.bind(this))
    }

    //! Component Did Update
    componentDidUpdate(prevProps, prevState) {
        this.queryPopupsCheckAndOpen(prevProps)
    }

    //! Component Will Unmount
    componentWillUnmount() {
        window.addEventListener('resize', this.screenResize)
        this.props.router.events.off('routeChangeComplete', this.queryPopupsCheckAndClose.bind(this))
    }

    render() {
        return (
            <UIContext.Provider value={{ ...this.state, ...this.methods }}>
                <ThemeProvider theme={theme}>
                    <ConfigProvider form={{ validateMessages }}>
                        <HelperClass />
                        <PopupStyle />
                        <Variables />
                        <Typography />
                        <FormStyle />
                        {this.props.children}
                    </ConfigProvider>
                </ThemeProvider>
            </UIContext.Provider>
        )
    }
}

export default withRouter(UIProvider)
